<template>
  <div class="lorentz-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$L = L₀ √(1 - β²)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>速度 v (c)</label>
            <span class="val">{{ vRatio.toFixed(3) }} c</span>
          </div>
          <div class="hud-item rose">
             <label>收缩后长度 L</label>
             <span class="val">{{ contractedLength.toFixed(1) }} px</span>
          </div>
          <div class="hud-item orange">
             <label>洛伦兹因子 γ</label>
             <span class="val">{{ gamma.toFixed(2) }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <!-- 静止参照系网格 -->
          <line v-for="i in 10" :key="'h'+i" x1="0" :y1="i*24" x2="400" :y2="i*24" stroke="#1e293b" stroke-width="0.5" />
          <line v-for="i in 16" :key="'v'+i" :x1="i*25" y1="0" :x2="i*25" y2="240" stroke="#1e293b" stroke-width="0.5" />

          <!-- 宇宙飞船 (随速度移动) -->
          <g :transform="`translate(${shipPos}, 100)`">
             <!-- 飞船本体 (应用长度收缩) -->
             <path :d="`M 0,0 L ${contractedLength},0 L ${contractedLength + 10},10 L ${contractedLength},-10 Z`" 
                   fill="#3b82f6" fill-opacity="0.8" stroke="#60a5fa" stroke-width="2" />
             <!-- 长度虚线标注 -->
             <line x1="0" y1="20" :x2="contractedLength" y2="20" stroke="#fbbf24" stroke-width="1" stroke-dasharray="2,2" />
             <text :x="contractedLength/2" y="35" text-anchor="middle" fill="#fbbf24" font-size="10">收缩长度 L</text>
             
             <!-- 静止长度 L0 阴影对比 -->
             <rect x="0" y="-15" :width="L0" height="30" fill="rgba(148, 163, 184, 0.1)" stroke="rgba(148, 163, 184, 0.2)" stroke-dasharray="4,2" />
             <text :x="L0/2" y="-25" text-anchor="middle" fill="#94a3b8" font-size="9">固有长度 L₀</text>
          </g>
        </svg>

        <div class="physics-caption">
          <strong>物理规律：</strong>当物体高速运动时，在静止观察者看来，物体的长度会沿着运动方向发生收缩。这一现象并非视觉幻象，而是<strong>时空自身性质的反映</strong>。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="label-row"><span class="label">调节飞船航行速度 (c)</span></div>
           <el-slider v-model="vRatio" :min="0" :max="0.999" :step="0.001" size="small"></el-slider>
        </div>
        
        <div class="discovery-card-discovery" v-latex>
           <i class="el-icon-timer"></i>
           <p><strong>实战物理：</strong>相对论不仅影响长度，还影响时间。<strong>观察：</strong>随着速度接近 0.9 c，飞船长度会剧烈缩短。当 v = 1.0 c（光速）时，飞船的长度将收缩为零（由点电荷极限决定）。这展示了<strong>绝对空间的不存在性</strong>。
           </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'LorentzContractionViz',
  data() {
    return {
      vRatio: 0.6,
      L0: 100, // 固有长度 (px)
      shipPos: 0,
      time: 0
    }
  },
  computed: {
    gamma() {
      return 1 / Math.sqrt(1 - Math.pow(this.vRatio, 2));
    },
    contractedLength() {
      return this.L0 / this.gamma;
    }
  },
  mounted() {
    this.animate();
  },
  methods: {
    animate() {
       requestAnimationFrame(this.animate);
       this.time += 0.01;
       // 循环移动
       const speedScale = this.vRatio * 15;
       this.shipPos += speedScale;
       if (this.shipPos > 400) this.shipPos = -150;
    }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.lorentz-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
</style>
